import React, { useMemo, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useFormContext, useWatch } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { Box, Button, Popover, Stack, TextField, Typography } from '@mui/material';

// Error boundary to prevent component unmounting
class ErrorBoundary extends React.Component {
    state = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error('ErrorBoundary caught:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return <div>Editor failed to render. Please refresh or try again.</div>;
        }
        return this.props.children;
    }
}

// Custom clipboard module to enforce plain text pasting
const Clipboard = Quill.import('modules/clipboard');
class PlainClipboard extends Clipboard {
    onPaste(rangeOrEvent, pastedContent = {}) {
        const quill = this.quill;

        // Quill v2 calls onPaste(range, { text, html }). Force plain text insertion.
        if (rangeOrEvent && typeof rangeOrEvent.index === 'number') {
            const range = rangeOrEvent;
            const text = typeof pastedContent.text === 'string' ? pastedContent.text : '';

            quill.deleteText(range.index, range.length, Quill.sources.USER);
            quill.insertText(range.index, text, Quill.sources.USER);
            quill.setSelection(range.index + text.length, Quill.sources.SILENT);
            quill.scrollSelectionIntoView();
            return;
        }

        // Backward-compatible fallback for event-based signatures.
        const event = rangeOrEvent;
        if (!event || typeof event.preventDefault !== 'function') {
            return;
        }

        event.preventDefault();
        const range = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
        const text = event.clipboardData?.getData('text/plain') || '';

        quill.deleteText(range.index, range.length, Quill.sources.USER);
        quill.insertText(range.index, text, Quill.sources.USER);
        quill.setSelection(range.index + text.length, Quill.sources.SILENT);
        quill.scrollSelectionIntoView();
    }
}
Quill.register('modules/clipboard', PlainClipboard, true);

const normalizeLink = (value) => {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
        return '';
    }

    if (/^(mailto:|tel:|https?:\/\/|\/)/i.test(trimmedValue)) {
        return trimmedValue;
    }

    return `https://${trimmedValue}`;
};

const RichTextEditor = ({ fieldName, onChange }) => {
    const { control } = useFormContext();
    const defaultValue = useWatch({ control, name: fieldName }) || '';
    const quillRef = useRef(null);
    const selectionRef = useRef(null);
    const [linkAnchorEl, setLinkAnchorEl] = useState(null);
    const [linkValue, setLinkValue] = useState('');
    const [linkText, setLinkText] = useState('');

    const generateToolbar = () => {
        const toolbar = [
            ['bold', 'italic', 'underline'],
            ['link'],
        ];
        return toolbar;
    };

    const handleOpenLinkDialog = () => {
        if (!quillRef.current) {
            return;
        }

        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
        const formats = editor.getFormat(range);
        const selectedText = range.length > 0
            ? editor.getText(range.index, range.length).replace(/\n$/, '')
            : '';
        const toolbarButton = editor.container.parentElement?.querySelector('.ql-toolbar .ql-link');

        selectionRef.current = range;
        setLinkValue(formats.link || '');
        setLinkText(selectedText);
        setLinkAnchorEl(toolbarButton || editor.container);
    };

    const modules = useMemo(() => ({
        toolbar: {
            container: generateToolbar(),
            handlers: {
                link: handleOpenLinkDialog
            }
        },
        clipboard: {
            matchers: [],
        },
    }), []);


    // Remove all formatting from the editor
    const handleRemoveFormatting = () => {
        if (quillRef.current) {
            const editor = quillRef.current.getEditor();
            const plainText = editor.getText();
            editor.setContents([{ insert: plainText }], 'user');
        }
    };

    const handleCloseLinkDialog = () => {
        setLinkAnchorEl(null);
        setLinkValue('');
        setLinkText('');
    };

    const handleSaveLink = () => {
        if (!quillRef.current) {
            handleCloseLinkDialog();
            return;
        }

        const editor = quillRef.current.getEditor();
        const range = selectionRef.current || editor.getSelection(true) || { index: editor.getLength(), length: 0 };
        const normalizedLink = normalizeLink(linkValue);
        const selectedText = range.length > 0
            ? editor.getText(range.index, range.length).replace(/\n$/, '')
            : '';
        const displayText = linkText.trim() || selectedText || normalizedLink;

        editor.focus();
        editor.setSelection(range.index, range.length, 'user');

        if (!normalizedLink) {
            handleCloseLinkDialog();
            return;
        }

        if (range.length > 0) {
            editor.deleteText(range.index, range.length, 'user');
            editor.insertText(range.index, displayText, 'link', normalizedLink, 'user');
            editor.setSelection(range.index + displayText.length, 0, 'user');
        } else {
            editor.insertText(range.index, displayText, 'link', normalizedLink, 'user');
            editor.setSelection(range.index + displayText.length, 0, 'user');
        }

        handleCloseLinkDialog();
    };

    return (
        <Controller
            name={fieldName}
            control={control}
            defaultValue={defaultValue}
            render={({ field }) => (
                <Box
                    sx={{
                        position: 'relative',
                        '& .ql-tooltip': {
                            display: 'none !important',
                        },
                    }}
                >
                    <ErrorBoundary>
                        <ReactQuill
                            {...field}
                            value={field.value || ''}
                            onChange={(content, delta, source, editor) => {
                                field.onChange(content); // Store full HTML to preserve manual formatting
                                if (onChange) onChange(fieldName, content); // Trigger custom onChange
                            }}
                            modules={modules}
                            ref={quillRef}
                        />
                    </ErrorBoundary>
                    <div style={{ marginTop: '8px' }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleRemoveFormatting}
                        >
                            Poista nykyiset tyylittelyt
                        </Button>
                    </div>
                    <Popover
                        open={Boolean(linkAnchorEl)}
                        anchorEl={linkAnchorEl}
                        onClose={handleCloseLinkDialog}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left'
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left'
                        }}
                        slotProps={{
                            paper: {
                                sx: {
                                    mt: 1,
                                    width: 340,
                                    maxWidth: 'calc(100vw - 32px)',
                                    backgroundColor: '#ffffff',
                                    opacity: 1,
                                    border: '1px solid #cbd5e1',
                                    boxShadow: '0 18px 40px rgba(15, 23, 42, 0.24)',
                                    borderRadius: 2,
                                    p: 2,
                                    pointerEvents: 'auto'
                                }
                            }
                        }}
                    >
                        <Stack spacing={1.5}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Lisää linkki
                            </Typography>
                            <TextField
                                autoFocus
                                fullWidth
                                size="small"
                                label="Linkin teksti"
                                placeholder="Näkyvä teksti"
                                value={linkText}
                                onChange={(event) => setLinkText(event.target.value)}
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label="Linkin osoite"
                                placeholder="https://esimerkki.fi"
                                value={linkValue}
                                onChange={(event) => setLinkValue(event.target.value)}
                            />
                            <Stack direction="row" justifyContent="flex-end" spacing={1}>
                                <Button onClick={handleCloseLinkDialog}>Peruuta</Button>
                                <Button onClick={handleSaveLink} variant="contained">Tallenna</Button>
                            </Stack>
                        </Stack>
                    </Popover>
                </Box>
            )}
        />
    );
};

export default RichTextEditor;
